import { t } from 'i18next';
import type { LabelColor } from '@components/Label';
import type {
  MonthlyBillingDTO,
  BillingStatus,
  MonthlyBillingUpdateDTO,
  StatusUpdateRq,
} from '../monthlyBilling.types';
import type { PlanScope } from '@repositories/softwareRepository/software.types';
import { SoftwareProviderModel } from '@repositories/softwareProviderRepository/models/SoftwareProviderModel';
import { SubscriptionSnapshotModel } from '@models/SubscriptionSnapshotModel';
import { FileSimpleModel } from '@repositories/fileRepository/models/FileSimpleModel';

export class SubscriptionBillingModel {
  private readonly _dto: MonthlyBillingDTO;
  private readonly _subscription: SubscriptionSnapshotModel;
  private readonly _subSubscriptionBillings: SubscriptionBillingModel[];
  private readonly _attachments: FileSimpleModel[];
  private readonly _provider?: SoftwareProviderModel;

  constructor(dto: MonthlyBillingDTO) {
    this._dto = dto;
    this._subscription = new SubscriptionSnapshotModel(dto.subscription);
    this._subSubscriptionBillings = dto?.subSubscriptionBillings?.length
      ? dto?.subSubscriptionBillings.map(sub => new SubscriptionBillingModel(sub))
      : [];
    this._attachments = dto.attachments ? dto.attachments.map(dto => new FileSimpleModel(dto)) : [];
    this._provider = dto.provider ? new SoftwareProviderModel(dto.provider) : undefined;
  }

  get originDTO(): MonthlyBillingDTO {
    return this._dto;
  }

  get billingDate() {
    return this._dto.billingDate;
  }

  get cost() {
    return this._dto.cost;
  }

  get costDiff() {
    return this._dto.costDiff;
  }

  get createDate() {
    return this._dto.createDate;
  }

  get updatedDate() {
    return this._dto.updatedDate;
  }

  get currencyUnit() {
    return this._dto.currencyUnit;
  }

  get exchangeRate() {
    return this._dto.exchangeRate;
  }

  get id() {
    return this._dto.id;
  }

  get moduleUsageCosts() {
    return this._dto.moduleUsageCosts;
  }

  get month() {
    return this._dto.month;
  }

  get originalCost() {
    return this._dto.originalCost;
  }

  get originalCostDiff() {
    return this._dto.originalCostDiff;
  }

  get originalCurrencyUnit() {
    return this._dto.originalCurrencyUnit;
  }

  get paymentDate() {
    return this._dto.paymentDate;
  }

  get provider(): SoftwareProviderModel | undefined {
    return this._provider ?? undefined;
  }

  get status() {
    return this._dto.status;
  }

  get subscription() {
    return this._subscription;
  }

  get usage() {
    return this._dto.usage;
  }

  get startDate() {
    return this._dto.startDate;
  }

  get endDate() {
    return this._dto.endDate;
  }

  get subSubscriptionBillings() {
    return this._subSubscriptionBillings;
  }

  get version() {
    return this._dto.version;
  }

  get isPayment(): boolean {
    return this._dto.status === 'PAID';
  }
  //  external 조건 추가 필요
  get isManuallyEdited(): boolean {
    return !!(
      this.isExternalSubscription &&
      this._dto.updatedBy &&
      this._dto.updatedBy !== '00000000-0000-0000-0000-000000000000'
    );
  }

  get isExternalSubscription() {
    return this.subscription.isExternalSubscription;
  }

  get isLatest(): boolean {
    return this._dto.isLatest;
  }

  get subscriptionId(): string {
    return this._dto.subscriptionId;
  }

  get isForecasted(): boolean {
    return this.status === 'FORECASTED';
  }

  get attachments() {
    return this._attachments;
  }

  get isConsolidatedBilling(): boolean {
    return !!this._dto.tenantBillingId || this.planScope === 'TENANT';
  }

  get planScope(): PlanScope {
    return this._subscription?.productPlan?.planScope || 'SUBSCRIPTION';
  }

  get statusLabel(): string {
    switch (this.status) {
      case 'CREATED':
        return t('Billing.status.CREATED');
      case 'BILLED':
        return t('Billing.status.BILLED');
      case 'PAID':
        return t('Billing.status.PAID');
      case 'FORECASTED':
        return t('Billing.status.FORECASTED');
      default:
        return t('Billing.status.CREATED');
    }
  }

  get statusLabelColor(): LabelColor {
    switch (this.status) {
      case 'CREATED':
        return 'green';
      case 'BILLED':
        return 'blue';
      case 'PAID':
        return 'gray';
      default:
        return 'yellow';
    }
  }

  getUpdateDTO(updateValue: Partial<MonthlyBillingUpdateDTO>): MonthlyBillingUpdateDTO {
    return {
      billingDate: this._dto.billingDate ?? null,
      cost: this._dto.cost,
      currencyUnit: this._dto.currencyUnit,
      endDate: this._dto.endDate ?? null,
      exchangeRate: this._dto.exchangeRate ?? null,
      id: this._dto.id,
      moduleUsageCosts: this._dto.moduleUsageCosts ?? [],
      month: this._dto.month,
      originalCost: this._dto.originalCost,
      originalCurrencyUnit: this._dto.originalCurrencyUnit,
      paymentDate: this._dto.paymentDate ?? null,
      startDate: this._dto.startDate ?? null,
      status: this._dto.status ?? null,
      subscriptionId: this._dto.subscription.subscriptionId,
      subscriptionVersion: this._dto.subscription.subscriptionVersion ?? null,
      ...updateValue,
    };
  }

  getUpdateStatusDTO(status: BillingStatus): StatusUpdateRq {
    return {
      id: this._dto.id,
      status,
    };
  }
}
